/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI安全台账图片上传Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-09-09 21:32:59
 */
import { useState, useCallback } from 'react'
import { message } from 'antd'
import { v4 as uuid } from 'uuid'
import { useAppDispatch } from '../../../app/hooks'
import { uploadToOSS, fetchUploadToken } from '../../../actions/main'
import { capturePromiseException } from '../../../utils/sentryUtils'
import type { RecognitionRecord } from '../types/contextInterface'

/**
 * 上传状态接口
 */
export interface XCUploadState {
  uploading: boolean
  imageUrl: string | null
  progress: number
}

/**
 * 记录上传状态映射接口
 */
export interface XCRecordUploadStates {
  [recordId: string]: XCUploadState
}

/**
 * 上传结果接口
 */
export interface XCUploadResult {
  success: boolean
  url?: string
  error?: string
}

/**
 * 图片上传Hook
 * @param onUploadSuccess - 上传成功回调
 * @returns 上传相关状态和方法
 */
export const useUploadImage = (
  onUploadSuccess?: (record: RecognitionRecord, imageUrl: string) => void
) => {
  const dispatch = useAppDispatch()
  const [recordUploadStates, setRecordUploadStates] = useState<
    XCRecordUploadStates
  >({})

  /**
   * 获取指定记录的上传状态
   * @param recordId - 记录ID
   * @returns 上传状态
   */
  const getUploadState = useCallback(
    (recordId: string): XCUploadState => {
      return (
        recordUploadStates[recordId] || {
          uploading: false,
          imageUrl: null,
          progress: 0
        }
      )
    },
    [recordUploadStates]
  )

  /**
   * 设置指定记录的上传状态
   * @param recordId - 记录ID
   * @param state - 上传状态
   */
  const setUploadState = useCallback(
    (recordId: string, state: Partial<XCUploadState>) => {
      setRecordUploadStates((prev) => ({
        ...prev,
        [recordId]: {
          ...prev[recordId],
          uploading: false,
          imageUrl: null,
          progress: 0,
          ...state
        }
      }))
    },
    []
  )

  const bucketName = 'net-cloud'

  /**
   * 文件上传前验证
   * @param file - 上传的文件
   * @returns 是否通过验证
   */
  const beforeUpload = useCallback((file: File): boolean => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 格式的图片!')
      return false
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB!')
      return false
    }
    return true
  }, [])

  /**
   * 上传图片到OSS
   * @param file - 文件对象
   * @param record - 记录对象
   * @returns 上传结果
   */
  const uploadImage = useCallback(
    async (file: File, record: RecognitionRecord): Promise<XCUploadResult> => {
      if (!beforeUpload(file)) {
        return { success: false, error: '文件验证失败' }
      }

      const recordId = record.id?.toString() || 'temp'
      setUploadState(recordId, {
        uploading: true,
        imageUrl: null,
        progress: 0
      })

      try {
        // 获取OSS上传token
        let ossToken = JSON.parse(localStorage.getItem('ossData') || '{}')
        if (
          !ossToken ||
          !ossToken[bucketName] ||
          ossToken[bucketName].expire < Date.now() / 1000
        ) {
          ossToken = await dispatch(fetchUploadToken())
        }

        // 生成唯一文件名
        const filename = uuid() + '.' + file.name.split('.').pop()
        const ossKey = `aiSecurity/photos/${filename}`

        // 上传到OSS
        await uploadToOSS({
          key: ossKey,
          OSSAccessKeyId: ossToken[bucketName]['accessid'],
          policy: ossToken[bucketName]['policy'],
          Signature: ossToken[bucketName]['signature'],
          file: file
        })

        const imageUrl = `http://net-cloud.xhey.top/${ossKey}?x-oss-process=image/resize,w_400`

        const recordId = record.id?.toString() || 'temp'
        setUploadState(recordId, {
          uploading: false,
          imageUrl,
          progress: 100
        })

        // 调用成功回调
        if (onUploadSuccess) {
          onUploadSuccess(record, imageUrl)
        }
        console.log('图片上传成功:', imageUrl)
        return { success: true, url: imageUrl }
      } catch (error) {
        console.error('图片上传失败:', error)
        const recordId = record.id?.toString() || 'temp'
        setUploadState(recordId, {
          uploading: false,
          imageUrl: null,
          progress: 0
        })

        message.error('图片上传失败!')
        capturePromiseException(error)
        return { success: false, error: '上传失败' }
      }
    },
    [onUploadSuccess]
  )

  /**
   * 处理文件变化
   * @param info - 上传信息
   * @param record - 当前记录
   */
  const handleChange = useCallback(
    async (info: any, record: RecognitionRecord) => {
      const { file } = info

      // 由于使用了 customRequest，文件状态会立即变为 done
      // 我们需要手动处理实际的上传逻辑
      if (file.status === 'done' && file.originFileObj) {
        console.log('开始上传文件:', file.name)

        // 调用实际的上传函数
        const result = await uploadImage(file.originFileObj, record)

        if (result.success && result.url) {
          console.log('图片上传成功，URL:', result.url)

          // 更新记录的 photoFilesAfter 字段
          record.photoFilesAfter = [result.url]

          // 显示图片
          const recordId = record.id?.toString() || 'temp'
          setUploadState(recordId, {
            uploading: false,
            imageUrl: result.url,
            progress: 100
          })
        } else {
          console.error('图片上传失败:', result.error)
          const recordId = record.id?.toString() || 'temp'
          setUploadState(recordId, {
            uploading: false,
            imageUrl: null,
            progress: 0
          })
        }
      }
    },
    [uploadImage]
  )

  /**
   * 重置指定记录的上传状态
   * @param recordId - 记录ID
   */
  const resetUpload = useCallback(
    (recordId?: string) => {
      if (recordId) {
        setUploadState(recordId, {
          uploading: false,
          imageUrl: null,
          progress: 0
        })
      } else {
        setRecordUploadStates({})
      }
    },
    [setUploadState]
  )

  /**
   * 设置指定记录的图片URL（用于显示已有图片）
   * @param recordId - 记录ID
   * @param url - 图片URL
   */
  const setImageUrl = useCallback(
    (recordId: string, url: string | null) => {
      setUploadState(recordId, {
        imageUrl: url
      })
    },
    [setUploadState]
  )

  return {
    getUploadState,
    uploadImage,
    handleChange,
    resetUpload,
    setImageUrl
  }
}

export default useUploadImage
